/**
 * App component sets up the main structure and routing for the application.
 * It uses React Router to manage different routes and displays corresponding components for each route.
 * 
 * The following components and styles are imported and used:
 * - Footer: Footer component
 * - Header: Header component
 * - Home: Home page component
 * - Quiz: Quiz page component
 * - About: About page component
 * - Articles: Articles page component
 * - Guides: Guides page component
 * - Specialties: Specialties page component
 * - Research: Research page component
 * - Theory: Theory page component
 * - FooterStyles.css: CSS styles for the Footer component
 * - HeaderStyles.css: CSS styles for the Header component
 * - QuestionStyles.css: CSS styles for Question and Quiz components
 * 
 * Routes:
 * - "/": Home component
 * - "/about": About component
 * - "/quiz": Quiz component
 * - "/articles": Articles component
 * - "/guides": Guides component
 * - "/specialties": Specialties component
 * - "/research": Research component
 * - "/theory": Theory component
 * 
 * return (
 *   <App />
 * )
 */
// src/App.js
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext'; // Remove UserContext from import
import { getCurrentUser, fetchUserAttributes } from './utils/auth'; // Import utility functions
import { Authenticator } from '@aws-amplify/ui-react';
import Footer from './components/layout/Footer';
import Header from './components/layout/Header';
import Home from './pages/Home';
import Quiz from './pages/Quiz';
import About from './pages/About';
// Removed unused Guides import
import Specialties from './pages/Specialties';
import Research from './pages/Research';
import Theory from './pages/Theory';
import Login from './components/features/auth/Login';
import Results from './components/features/Results/Results';
import Pathfinder from './pages/Pathfinder';
import PathfinderSetup from './pages/PathfinderSetup'; // Import PathfinderSetup
import Competition from './pages/CompetitionRatios'
import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
import RequireAuth from './components/RequireAuth'; // Import the RequireAuth component
import PersonalityTypes from './pages/PersonalityTypes'; // Import the new component
import UserPage from './pages/UserPage';
import MedicalQuizPage from './pages/MedicalQuizPage';
import QuizChoice from './pages/QuizChoice'; // Import the new component
import CVGen from './pages/CVGen';
import PremiumSuites from './pages/PremiumSuites'; // Import the new PremiumSuites component
import DreamSpecialty from './components/features/DreamSpecialty/DreamSpecialty'; // Import the new DreamSpecialty component
import SubscriptionTestPage from './pages/SubscriptionTestPage'; // Import the SubscriptionTestPage component
import PaymentPage from './pages/PaymentPage';
import FeedbackWidget from './components/features/Feedback/FeedbackWidget'; // Add this import
import LandingPage from './components/LandingPage'; // Import the new LandingPage component
import AccountCreate from './pages/account_create'; // Import AccountCreate component
import RealBilling from './pages/RealBilling'; // Import RealBilling component
import CSTTeachingPDF from './pages/CSTTeachingPDF'; // Import CSTTeachingPDF component

Amplify.configure(awsconfig);

function ScrollToTop() {
  const location = useLocation();

  // Prevent browser from restoring scroll on back/forward
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Force top on every navigation (runs before paint)
  useLayoutEffect(() => {
    const top = 0;
    const left = 0;

    // Cover both scrolling roots + window
    document.documentElement.scrollTop = top;
    document.body.scrollTop = top;
    window.scrollTo({ top, left, behavior: 'auto' });
  }, [location.key, location.pathname, location.search]);

  return null;
}

const Layout = ({ children }) => {
  const location = useLocation();
  const isPdfPage = location.pathname === '/PDF_CST_Teaching';

  return (
    <>
      {!isPdfPage && <Header />}
      {children}
      {!isPdfPage && <Footer />}
      {!isPdfPage && <FeedbackWidget />}
    </>
  );
};

function App() {
  const [globalUser, setGlobalUser] = useState(null);

  useEffect(() => {
    const initUser = async () => {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        setGlobalUser({ ...user, attributes });
      } catch (err) {
        console.error('Auth error:', err);
      }
    };
    initUser();
  }, []);

  return (
    <UserProvider value={globalUser}>
      <Authenticator.Provider>
        <Router>
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Root path now uses the LandingPage router component */}
              <Route path="/" element={<LandingPage />} />
              
              {/* Add explicit route for Home to maintain direct navigation */}
              <Route path="/home" element={<Home />} />
              
              {/* PDF Route */}
              <Route path="/PDF_CST_Teaching" element={<CSTTeachingPDF />} />

              {/* Existing routes */}
              <Route path="/pathfinder" element={<Pathfinder />} />
            <Route path="/quiz-choice" element={<QuizChoice />} />
            <Route path="/about" element={<About />} />
            <Route path="/results" element={<Results />} />
            {/* <Route path="/guides" element={<Guides />} /> */}
            <Route path="/specialties" element={<Specialties />} />
            <Route path="/research" element={<Research />} />
            <Route path="/theory" element={<Theory />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/competition-ratios" element={<Competition />} />
            <Route path="/personality-types" element={<PersonalityTypes />} />
            <Route path="/login" element={<Login />} /> {/* Login route */}
            <Route path="/user" element={<UserPage userID="12345" />} />
            <Route path="/medmatch" element={<MedicalQuizPage/>} />
            <Route path="/quiz-choice" element={<QuizChoice />} /> {/* Add this route */}
            <Route path="/cv-gen" element={<CVGen />} /> {/* Add this route */}
            <Route path="/premium-suites" element={<PremiumSuites />} /> {/* Add the new route for PremiumSuites */}
            {/* <Route path="/payment" element={<PaymentPage />} /> */}
            {/* <Route path="/billing/test" element={<SubscriptionTestPage />} /> */}
            <Route path="/billing" element={<RealBilling />} /> {/* Add this route */}

            {/* Add PathfinderSetup route */}
            <Route path="/pathfinder-setup" element={
              <RequireAuth>
                <PathfinderSetup />
              </RequireAuth>
            } />
            
            {/* Protected routes */}
            <Route
              path="/pathfinder"
              element={
                <RequireAuth>
                  <Pathfinder />
                </RequireAuth>
              }
            />
            <Route
              path="/dream-specialty"
              element={<DreamSpecialty />}
            />
            <Route
              path="/account_create"
              element={
                <RequireAuth>
                  <AccountCreate />
                </RequireAuth>
              }
            />
          </Routes>
          </Layout>
        </Router>
      </Authenticator.Provider>
    </UserProvider>
  );
}

export default App;
